import type { ReactNode } from 'react';
import { cx } from './cx';
import styles from './ProgressRing.module.css';

interface ProgressRingProps {
  readonly size: number;
  readonly radius: number;
  readonly strokeWidth: number;
  /** Portion of the ring drawn, 0–1, starting from 12 o'clock. */
  readonly fraction: number;
  readonly tone: 'warning' | 'danger';
  readonly className?: string | undefined;
  readonly contentClassName?: string | undefined;
  readonly children: ReactNode;
}

export function ProgressRing({
  size,
  radius,
  strokeWidth,
  fraction,
  tone,
  className,
  contentClassName,
  children,
}: ProgressRingProps) {
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(1, Math.max(0, fraction));

  return (
    <div className={cx(styles.ring, className)} style={{ width: size, height: size }}>
      <svg
        className={styles.svg}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
      >
        <circle
          className={styles.track}
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
        />
        <circle
          className={cx(styles.progress, styles[tone])}
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className={cx(styles.content, contentClassName)}>{children}</div>
    </div>
  );
}
